'use client';
import { Separator } from '@ahafood/ui/components/separator';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { cn } from '@ahm/common-helpers';
import { useInfiniteQuery } from '@tanstack/react-query';
import React, { Fragment, useEffect, useRef, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import type { Payload } from './simple-store-list';
import StoreCard from './store-card';
import StoreEmptyState from './store-empty-state';
import {
  DEFAULT_STORE_RADIUS,
  getStoresFn,
  type Store,
} from '@/api/ahafood/get-store-fn';
import { useCityLocation } from '@/hooks/use-city-location';
import { trackEvent } from '@/lib/event-tracking';
import { createSearchSession } from '@/lib/search-session';
import { useAuthStore } from '@/store';

type StoreListProps = {
  data: Store[];
  payload: Payload;
};

export default function StoreList({ data, payload }: StoreListProps) {
  const { user } = useAuthStore();
  // Toạ độ chỉ dùng được khi địa chỉ thuộc đúng city đang duyệt (useCityLocation).
  const { lat, lng, hasLocation } = useCityLocation();
  const limit = 20;

  // Add tracking ref for search events
  const lastLoggedQuery = useRef<string | undefined>(undefined);

  const {
    data: storesData,
    fetchNextPage,
    hasNextPage,
    isPending,
    isFetched,
  } = useInfiniteQuery({
    queryKey: ['stores', payload, lat, lng],
    queryFn: async ({ pageParam = 0 }) => {
      return getStoresFn({
        ...payload,
        limit,
        skip: pageParam,
        // radius is REQUIRED whenever lat/lng is sent. Respect a filter-provided
        // radius (e.g. "gần tôi" = 5000) and fall back to the shared default.
        ...(hasLocation
          ? { lat, lng, radius: payload.radius ?? DEFAULT_STORE_RADIUS }
          : {}),
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.stores) return undefined;
      const loaded = allPages.reduce(
        (acc, page) => acc + (page?.stores?.length || 0),
        0
      );
      return lastPage.stores.length < limit ? undefined : loaded;
    },
    // Only seed SSR data when there is NO client location. If the user has an
    // address, the SSR list may reflect a different (or no) location, so we must
    // fetch fresh nearby results instead of showing stale city-based data.
    initialData:
      !hasLocation && data?.length
        ? {
            pages: [{ stores: data, total: data.length }],
            pageParams: [0],
          }
        : undefined,
    // city_id is required by the API — don't fire until it's resolved.
    enabled: Boolean(payload?.city_id),
  });

  const stores = useMemo(() => {
    return (
      storesData?.pages?.flatMap((page) => page.stores).filter(Boolean) ?? []
    );
  }, [storesData?.pages]);

  // Apply filters using useMemo to avoid re-creating on every render
  const filteredStores = useMemo(() => {
    let result = stores;

    if (payload?.promotion) {
      result = result.filter((store) =>
        store.ai_tags?.some((tag) => tag.toLowerCase().includes('deal'))
      );
    }
    if (payload?.freeship === 'freeship') {
      // Filter for any freeship (50% or 100%)
      result = result.filter((store) =>
        store.ai_tags?.some((tag) => tag.toLowerCase().includes('freeship'))
      );
    } else if (payload?.freeship === 'freeship-100') {
      // Filter for specifically Freeship 100%
      result = result.filter((store) =>
        store.ai_tags?.some((tag) => tag.includes('Freeship 100%'))
      );
    }
    if (payload?.open_now) {
      result = result.filter((store) => store.is_temporary_closed === false);
    }

    return result;
  }, [stores, payload?.promotion, payload?.freeship, payload?.open_now]);

  // Log search event when payload.text changes and stores are loaded
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      payload.text &&
      payload.text !== lastLoggedQuery.current &&
      filteredStores.length > 0 &&
      user?.cunstomerId
    ) {
      setTimeout(() => {
        try {
          // Create search session and get search_id
          const searchId = createSearchSession(
            user.cunstomerId ?? '',
            payload.text ?? ''
          );

          // Create first 5 stores data with separate fields for GA compatibility
          const first5Stores = filteredStores.slice(0, 5);
          const eventData: Record<string, any> = {
            key_word: payload.text,
            user_id: user.cunstomerId,
            search_id: searchId,
            total_stores_found: filteredStores.length,
          };

          // Add each store as separate fields to avoid GA 100 character limit
          first5Stores.forEach((store, index) => {
            const position = index + 1;
            const storeItemIds =
              store.highlight_items
                ?.slice(0, 3)
                .map((item) => item._id)
                .join(',') ?? '';

            eventData[`first_store_${position}`] = JSON.stringify({
              s_id: store._id.toString(),
              p: position,
              i_id: storeItemIds,
            });
          });

          trackEvent('search_event', eventData);
          lastLoggedQuery.current = payload.text;
        } catch (e) {
          console.error('[StoreList] Error logging search results', e);
        }
      }, 0);
    }
  }, [payload.text, filteredStores, user?.cunstomerId]);

  // No results: distinguish "still loading" from "settled but empty".
  if (filteredStores.length === 0) {
    if (isPending || !isFetched) {
      return (
        <div
          className={cn(
            'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-6 lg:gap-8',
            'auto-rows-max items-start justify-items-stretch pb-20'
          )}
        >
          <LoadingSkeleton />
        </div>
      );
    }
    return (
      <StoreEmptyState
        variant={payload.text ? 'search' : 'nearby'}
        searchText={payload.text}
      />
    );
  }

  return (
    <>
      <InfiniteScroll
        className={cn(
          'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-6 lg:gap-8',
          'auto-rows-max items-start justify-items-stretch pb-20'
        )}
        dataLength={filteredStores.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<LoadingSkeleton />}
        endMessage={<p className="text-center"></p>}
      >
        {filteredStores.map((store, index) => (
          <Fragment key={store._id}>
            <StoreCard data={store} position={index + 1} />
            {index < filteredStores.length - 1 ? (
              <Separator className="sm:hidden" />
            ) : null}
          </Fragment>
        ))}
      </InfiniteScroll>
    </>
  );
}

function LoadingSkeleton() {
  return [...Array(4)].map((_, index) => (
    <div className="flex flex-row gap-3 py-1" key={index}>
      <Skeleton className="size-28 shrink-0 rounded-2xl" />
      <div className="flex-1 min-w-0 flex flex-col gap-2 py-1">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
        <div className="flex gap-1.5 mt-1">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
      </div>
    </div>
  ));
}
